// src/pages/TicketsPage.js
//
// Support tickets & feature requests.
// - Any staff member can submit a ticket and see the ones they filed ("My
//   Requests").
// - ADMIN/DIRECTOR additionally get a "Manage" tab: all tickets sorted by
//   priority, with a detail modal to set priority, assign, comment, and change
//   status. Resolving a ticket DMs the creator via in-app messaging; the
//   backend also emails a configured inbox when a new ticket is filed.
import { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert, Badge, Button, Form, Modal, Spinner, Table, Tabs, Tab,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";
import AutoGrowTextarea from "../components/AutoGrowTextarea";

const KIND_LABEL = { SUPPORT: "Support", FEATURE: "Feature" };
const PRIORITY_VARIANT = {
  LOW: "secondary",
  MEDIUM: "info",
  HIGH: "warning",
  URGENT: "danger",
};
const RESOLVED_STATUSES = new Set(["Resolved", "Shipped"]);

function statusVariant(status) {
  if (RESOLVED_STATUSES.has(status)) return "success";
  if (status === "Closed") return "secondary";
  if (status === "In Progress" || status === "Planned") return "primary";
  return "warning"; // Open
}

function PriorityBadge({ value }) {
  return <Badge bg={PRIORITY_VARIANT[value] || "secondary"}>{titleCase(value)}</Badge>;
}
function StatusBadge({ value }) {
  return <Badge bg={statusVariant(value)}>{value}</Badge>;
}
function titleCase(s) {
  return s ? s.charAt(0) + s.slice(1).toLowerCase() : s;
}
function fmt(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString([], {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

const EMPTY_NEW = { kind: "SUPPORT", title: "", description: "", priority: "MEDIUM" };

export default function TicketsPage() {
  const { user } = useContext(AuthContext);
  const [meta, setMeta] = useState(null);
  const canManage = !!meta?.can_manage;

  const [tab, setTab] = useState("mine");
  const [error, setError] = useState("");

  // Create modal
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_NEW);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Lists
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);
  const [loadingAll, setLoadingAll] = useState(false);

  // Manage filters
  const [fKind, setFKind] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [openOnly, setOpenOnly] = useState(true);

  // Detail modal
  const [detailId, setDetailId] = useState(null);

  const loadMeta = useCallback(async () => {
    try {
      const res = await api.get("/tickets/meta");
      setMeta(res.data);
    } catch {
      setError("Failed to load ticket settings.");
    }
  }, []);

  const loadMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      const res = await api.get("/tickets/mine");
      setMine(res.data.items || []);
    } catch {
      setError("Failed to load your requests.");
    } finally {
      setLoadingMine(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      const params = {};
      if (fKind) params.kind = fKind;
      if (fPriority) params.priority = fPriority;
      if (openOnly) params.open_only = 1;
      const res = await api.get("/tickets", { params });
      setAll(res.data.items || []);
    } catch {
      setError("Failed to load tickets.");
    } finally {
      setLoadingAll(false);
    }
  }, [fKind, fPriority, openOnly]);

  useEffect(() => { loadMeta(); loadMine(); }, [loadMeta, loadMine]);
  useEffect(() => { if (canManage && tab === "manage") loadAll(); }, [canManage, tab, loadAll]);

  function newField(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  async function submitNew(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setFormError("Title and description are required.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await api.post("/tickets", {
        kind: form.kind,
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
      });
      setShowNew(false);
      setForm(EMPTY_NEW);
      await loadMine();
      if (canManage && tab === "manage") await loadAll();
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to submit.");
    } finally {
      setSaving(false);
    }
  }

  function openNew() {
    setForm(EMPTY_NEW);
    setFormError("");
    setShowNew(true);
  }

  function onDetailSaved() {
    loadMine();
    if (canManage) loadAll();
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <h3 className="mb-0">
          <i className="bi bi-life-preserver me-2" />
          Support &amp; Feature Requests
        </h3>
        <Button size="sm" className="ms-auto" onClick={openNew}>
          <i className="bi bi-plus-lg me-1" />New Request
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}

      <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
        <Tab eventKey="mine" title="My Requests">
          <TicketList
            rows={mine}
            loading={loadingMine}
            empty="You haven't submitted any requests yet."
            onRowClick={(t) => setDetailId(t.id)}
            showSubmitter={false}
          />
        </Tab>

        {canManage && (
          <Tab eventKey="manage" title="Manage">
            <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
              <Form.Group>
                <Form.Label className="small text-muted mb-1">Type</Form.Label>
                <Form.Select size="sm" value={fKind} onChange={(e) => setFKind(e.target.value)}>
                  <option value="">All</option>
                  <option value="SUPPORT">Support</option>
                  <option value="FEATURE">Feature</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label className="small text-muted mb-1">Priority</Form.Label>
                <Form.Select size="sm" value={fPriority} onChange={(e) => setFPriority(e.target.value)}>
                  <option value="">All</option>
                  {(meta?.priorities || []).map((p) => (
                    <option key={p} value={p}>{titleCase(p)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Check
                type="switch"
                id="open-only"
                label="Hide closed"
                checked={openOnly}
                onChange={(e) => setOpenOnly(e.target.checked)}
                className="mb-1"
              />
            </div>
            <TicketList
              rows={all}
              loading={loadingAll}
              empty="No tickets match these filters."
              onRowClick={(t) => setDetailId(t.id)}
              showSubmitter={true}
            />
          </Tab>
        )}
      </Tabs>

      {/* New request modal */}
      <Modal show={showNew} onHide={() => setShowNew(false)} centered>
        <Form onSubmit={submitNew}>
          <Modal.Header closeButton>
            <Modal.Title>New Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger" className="py-2">{formError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select value={form.kind} onChange={newField("kind")}>
                <option value="SUPPORT">Support ticket (something's broken / I need help)</option>
                <option value="FEATURE">Feature request (I'd like the app to do…)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={form.title}
                onChange={newField("title")}
                placeholder="Short summary"
                autoFocus
                maxLength={255}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Details <span className="text-danger">*</span></Form.Label>
              <AutoGrowTextarea
                rows={5}
                value={form.description}
                onChange={newField("description")}
                placeholder="What happened, or what would you like? Include steps if it's a bug."
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Suggested priority</Form.Label>
              <Form.Select value={form.priority} onChange={newField("priority")}>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </Form.Select>
              <Form.Text className="text-muted">
                An admin may adjust the final priority.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Submit"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Detail modal */}
      {detailId != null && (
        <TicketDetailModal
          ticketId={detailId}
          canManage={canManage}
          meta={meta}
          currentUser={user}
          onHide={() => setDetailId(null)}
          onSaved={onDetailSaved}
        />
      )}
    </div>
  );
}

function TicketList({ rows, loading, empty, onRowClick, showSubmitter }) {
  if (loading) {
    return <div className="text-muted"><Spinner animation="border" size="sm" className="me-2" />Loading…</div>;
  }
  if (!rows.length) {
    return <p className="text-muted">{empty}</p>;
  }
  return (
    <Table hover responsive size="sm" className="align-middle">
      <thead>
        <tr>
          <th>Priority</th>
          <th>Type</th>
          <th>Title</th>
          <th>Status</th>
          {showSubmitter && <th>Submitted by</th>}
          <th>Created</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t) => (
          <tr key={t.id} style={{ cursor: "pointer" }} onClick={() => onRowClick(t)}>
            <td><PriorityBadge value={t.priority} /></td>
            <td>{KIND_LABEL[t.kind] || t.kind}</td>
            <td className="fw-semibold">
              {t.title}
              {t.note_count > 0 && (
                <span className="text-muted ms-2">
                  <i className="bi bi-chat-left-text" /> {t.note_count}
                </span>
              )}
            </td>
            <td><StatusBadge value={t.status} /></td>
            {showSubmitter && <td>{t.created_by_name || "—"}</td>}
            <td className="text-muted small">{fmt(t.created_at)}</td>
            <td className="text-end"><i className="bi bi-chevron-right text-muted" /></td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function TicketDetailModal({ ticketId, canManage, meta, currentUser, onHide, onSaved }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [assignees, setAssignees] = useState([]);
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  const [noteBody, setNoteBody] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tickets/${ticketId}`);
      const t = res.data;
      setTicket(t);
      setPriority(t.priority);
      setStatus(t.status);
      setAssignedTo(t.assigned_to ? String(t.assigned_to) : "");
      setResolutionNote(t.resolution_note || "");
    } catch {
      setErr("Failed to load ticket.");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canManage) return;
    api.get("/tickets/assignees").then((r) => setAssignees(r.data.items || [])).catch(() => {});
  }, [canManage]);

  const statusOptions = ticket ? (meta?.statuses?.[ticket.kind] || []) : [];
  const willResolve = RESOLVED_STATUSES.has(status) && !RESOLVED_STATUSES.has(ticket?.status);

  async function saveMeta() {
    setSavingMeta(true);
    setErr("");
    try {
      await api.patch(`/tickets/${ticketId}`, {
        priority,
        status,
        assigned_to: assignedTo ? Number(assignedTo) : null,
        resolution_note: resolutionNote,
      });
      await load();
      onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.error || "Failed to save changes.");
    } finally {
      setSavingMeta(false);
    }
  }

  async function addNote(e) {
    e.preventDefault();
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/tickets/${ticketId}/notes`, { body: noteBody.trim() });
      setNoteBody("");
      await load();
      onSaved?.();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Failed to add comment.");
    } finally {
      setSavingNote(false);
    }
  }

  return (
    <Modal show onHide={onHide} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          {ticket && <PriorityBadge value={ticket.priority} />}
          {ticket ? ticket.title : "Ticket"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger" className="py-2">{err}</Alert>}
        {loading || !ticket ? (
          <div className="text-muted"><Spinner animation="border" size="sm" className="me-2" />Loading…</div>
        ) : (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Badge bg="light" text="dark">{KIND_LABEL[ticket.kind] || ticket.kind}</Badge>
              <StatusBadge value={ticket.status} />
              <span className="text-muted small ms-auto">
                {ticket.created_by_name} · {fmt(ticket.created_at)}
              </span>
            </div>

            <p style={{ whiteSpace: "pre-wrap" }}>{ticket.description}</p>

            {ticket.assigned_to_name && (
              <p className="small text-muted mb-1">
                <i className="bi bi-person-check me-1" />Assigned to {ticket.assigned_to_name}
              </p>
            )}
            {ticket.resolution_note && (
              <Alert variant="success" className="py-2">
                <strong>Resolution:</strong> {ticket.resolution_note}
              </Alert>
            )}

            {/* Admin controls */}
            {canManage && (
              <div className="border rounded p-3 mb-3 bg-body-tertiary">
                <div className="row g-3">
                  <div className="col-sm-4">
                    <Form.Label className="small text-muted mb-1">Priority</Form.Label>
                    <Form.Select size="sm" value={priority} onChange={(e) => setPriority(e.target.value)}>
                      {(meta?.priorities || []).map((p) => (
                        <option key={p} value={p}>{titleCase(p)}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-sm-4">
                    <Form.Label className="small text-muted mb-1">Status</Form.Label>
                    <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                      {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Form.Select>
                  </div>
                  <div className="col-sm-4">
                    <Form.Label className="small text-muted mb-1">Assigned to</Form.Label>
                    <Form.Select size="sm" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                      <option value="">Unassigned</option>
                      {assignees.map((a) => (
                        <option key={a.id} value={a.id}>{a.full_name}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-12">
                    <Form.Label className="small text-muted mb-1">
                      Resolution note {willResolve && <span className="text-success">(sent to submitter)</span>}
                    </Form.Label>
                    <AutoGrowTextarea
                      rows={2}
                      size="sm"
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      placeholder="What was done / how it was resolved"
                    />
                  </div>
                </div>
                <div className="text-end mt-3">
                  <Button size="sm" onClick={saveMeta} disabled={savingMeta}>
                    {savingMeta ? <Spinner animation="border" size="sm" /> : "Save changes"}
                  </Button>
                </div>
                {willResolve && (
                  <p className="small text-success mt-2 mb-0">
                    <i className="bi bi-envelope me-1" />
                    Saving will notify {ticket.created_by_name} via in-app message.
                  </p>
                )}
              </div>
            )}

            {/* Comment thread */}
            <h6 className="text-muted">Comments</h6>
            {ticket.notes?.length ? (
              <div className="mb-3">
                {ticket.notes.map((n) => (
                  <div key={n.id} className="border-start ps-3 mb-2">
                    <div className="small">
                      <strong>{n.author_name || "Unknown"}</strong>
                      <span className="text-muted ms-2">{fmt(n.created_at)}</span>
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{n.body}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted small">No comments yet.</p>
            )}

            <Form onSubmit={addNote} className="d-flex gap-2 align-items-start">
              <AutoGrowTextarea
                rows={1}
                size="sm"
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                placeholder="Add a comment…"
              />
              <Button type="submit" size="sm" variant="outline-primary" disabled={savingNote || !noteBody.trim()}>
                {savingNote ? <Spinner animation="border" size="sm" /> : "Post"}
              </Button>
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
